import amplitudeService from '@features/analytics/amplitudeService';
import PricingCard from './PricingCard';
import {
  freeFeatures,
  teamPlusFeatures,
  proTeamFeatures,
} from './planFeatureList';
import type {PlanFeature} from './planFeatureList';
import {trpc} from '@features/utils/trpc';
import {useSubscription} from './subscriptionHook';
import {useCheckoutSession} from './useCheckoutSession';
import {UpgradeConfirmDialog} from '@features/ui/components/UpgradeConfirmDialog';
import {AnnualUpgradeConfirmDialog} from '@features/ui/components/AnnualUpgradeConfirmDialog';
import {TierUpgradeConfirmDialog} from '@features/ui/components/TierUpgradeConfirmDialog';
import {useState} from 'react';
import {useNavigate} from '@tanstack/react-router';
import {captureException} from '@sentry/react';
import type {BillingInterval} from './subscriptionService';
import type {SubscriptionTier} from './subscriptionTier';
import {
  pricing,
  discountPercent,
  yearlyMonthlyEquivalent,
  formatPriceLabel,
} from './pricing';
import {toast} from 'sonner';

export const FreeTeamCard = () => {
  const {subscriptions} = useSubscription();
  if (!subscriptions) {
    return null;
  }

  return (
    <PricingCard
      isCurrentPlan
      title='Team Free'
      planIcon='free'
      price='$0'
      period='/month'
      features={freeFeatures(subscriptions.settings)}
      buttonText='Current plan'
    />
  );
};

type CheckoutPlan =
  | 'team-plus'
  | 'team-plus-annual'
  | 'team-pro-unlimited'
  | 'team-pro-unlimited-annual';

interface TeamTierCardConfig {
  tier: 'plus' | 'pro';
  title: string;
  planIcon: 'plus' | 'pro';
  price: string;
  features: PlanFeature[];
  monthlyPlan: CheckoutPlan;
  annualPlan: CheckoutPlan;
  annualDialogPlanType: 'team-plus' | 'team-pro';
  amplitudeEvent: string;
  annualToggle: {
    monthlyPrice: string;
    yearlyPrice: string;
    yearlyMonthlyEquivalent: string;
    discountPercent: number;
  };
}

interface TeamTierCardProps {
  config: TeamTierCardConfig;
  isAnnual: boolean;
  billingInterval: BillingInterval | undefined;
  individualBillingInterval: BillingInterval | undefined;
  currentTier: SubscriptionTier;
}

const TeamTierCard = ({
  config,
  isAnnual,
  billingInterval,
  individualBillingInterval,
  currentTier,
}: TeamTierCardProps) => {
  const navigate = useNavigate();
  const trpcUtils = trpc.useUtils();
  const {effectiveSubscription} = useSubscription();
  const {data: billingDetails} = trpc.subscription.getBillingDetails.useQuery();
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [isAnnualConfirmOpen, setIsAnnualConfirmOpen] = useState(false);
  const [isTierUpgradeConfirmOpen, setIsTierUpgradeConfirmOpen] =
    useState(false);

  const myTeams = trpc.teams.getMyTeams.useQuery();
  const team = myTeams.data?.[0];
  const teamId = team?.id;
  const teamSeatsCount =
    (team?.members.length ?? 0) +
    (team?.invites.filter((i) => i.status === 'pending').length ?? 0);

  const createCheckoutSessionMutation = useCheckoutSession();
  const upgradeToTeam = trpc.subscription.upgradeToTeam.useMutation({
    onSuccess: () => {
      void trpcUtils.subscription.getSubscriptionInfo.invalidate();
    },
    onError: (error) => {
      captureException(error);
    },
  });
  const upgradeTeamToAnnual = trpc.subscription.upgradeTeamToAnnual.useMutation(
    {
      onSuccess: () => {
        void trpcUtils.subscription.getSubscriptionInfo.invalidate();
      },
      onError: (error) => {
        captureException(error);
      },
    },
  );
  const upgradeTeamToPro = trpc.subscription.upgradeTeamToPro.useMutation({
    onSuccess: () => {
      void trpcUtils.subscription.getSubscriptionInfo.invalidate();
    },
    onError: (error) => {
      captureException(error);
    },
  });

  const isOnYearly = billingInterval === 'year';
  const isOnMonthly = billingInterval === 'month';
  const isIndividualOnYearly = individualBillingInterval === 'year';
  const isOnTier = currentTier === config.tier;

  const canUpgradeToYearly = isOnTier && isOnMonthly && isAnnual;
  const isCurrentPlanDisplay =
    isOnTier && ((isOnYearly && isAnnual) || (isOnMonthly && !isAnnual));
  const isToggleDisabled = isOnYearly || isIndividualOnYearly;

  const isIndividualUpgrade =
    effectiveSubscription?.subscriptionSource === 'individual' &&
    effectiveSubscription.tier !== 'free';

  // Team Plus → Team Pro tier upgrade (issue #3)
  const canUpgradeTier =
    currentTier === 'plus' && config.tier === 'pro' && !isIndividualUpgrade;

  // Team Pro user viewing Team Plus card — disable to prevent downgrade (issue #7)
  const isLowerTier = currentTier === 'pro' && config.tier === 'plus';

  const hasIndividualPro = effectiveSubscription?.tier === 'pro';
  const isLowerThanIndiv = hasIndividualPro && config.tier !== 'pro';

  const handleTeamUpgrade = () => {
    amplitudeService.logEvent(config.amplitudeEvent, {
      subscriptionSource: effectiveSubscription?.subscriptionSource,
      isActive: effectiveSubscription?.isActive,
      page: 'subscription-options',
      hasExistingTeam: !!teamId,
      billingCycle: isAnnual ? 'yearly' : 'monthly',
    });

    if (canUpgradeToYearly && teamId) {
      setIsAnnualConfirmOpen(true);
      return;
    }

    if (canUpgradeTier && teamId) {
      setIsTierUpgradeConfirmOpen(true);
      return;
    }

    if (isIndividualUpgrade) {
      setIsConfirmOpen(true);
    } else {
      void createCheckoutSessionMutation.mutateAsync({
        plan: isAnnual ? config.annualPlan : config.monthlyPlan,
        teamId,
      });
    }
  };

  const getButtonText = () => {
    if (canUpgradeToYearly) return 'Upgrade';
    if (isCurrentPlanDisplay) return 'Current plan';
    if (isLowerTier) return 'Current plan';
    if (
      isIndividualUpgrade ||
      canUpgradeTier ||
      (effectiveSubscription?.tier && effectiveSubscription.tier !== 'free')
    )
      return 'Upgrade';
    return 'Continue';
  };

  if (isLowerTier || isLowerThanIndiv) {
    return null;
  }

  return (
    <>
      <PricingCard
        title={config.title}
        planIcon={config.planIcon}
        price={config.price}
        period='/seat/month'
        features={config.features}
        buttonText={getButtonText()}
        isCurrentPlan={isCurrentPlanDisplay}
        disabled={isLowerTier}
        buttonAction={handleTeamUpgrade}
        isLoading={
          upgradeTeamToAnnual.isPending ||
          upgradeTeamToPro.isPending ||
          (isIndividualUpgrade
            ? upgradeToTeam.isPending
            : createCheckoutSessionMutation.isPending)
        }
        annualToggle={
          // Yearly is only offered to users already on a yearly plan (team or
          // the individual sub being upgraded from). It renders locked on to show
          // the correct yearly pricing; everyone else is monthly-only, toggle hidden.
          isToggleDisabled
            ? {
                isAnnual,
                onToggle: () => {},
                ...config.annualToggle,
                perSeat: true,
                disabled: true,
              }
            : undefined
        }
      />

      <UpgradeConfirmDialog
        open={isConfirmOpen}
        onOpenChange={setIsConfirmOpen}
        onConfirm={() => {
          void upgradeToTeam
            .mutateAsync({teamId, isAnnual, targetTier: config.tier})
            .then(() => {
              setIsConfirmOpen(false);
              toast.success('Subscription upgraded!');
              void navigate({to: '/', replace: true});
            })
            .catch(() => {
              toast.error('Failed to upgrade subscription');
            });
        }}
        isLoading={upgradeToTeam.isPending}
        teamSeatsCount={teamSeatsCount || 1}
        isAnnual={isAnnual}
        currentBillingInterval={individualBillingInterval}
        targetTier={config.tier}
        sourceTier={effectiveSubscription?.tier === 'pro' ? 'pro' : 'plus'}
        currentPriceLabel={
          billingDetails
            ? formatPriceLabel(
                billingDetails.priceInCents,
                billingDetails.billingInterval,
              )
            : undefined
        }
      />

      <AnnualUpgradeConfirmDialog
        open={isAnnualConfirmOpen}
        onOpenChange={setIsAnnualConfirmOpen}
        onConfirm={() => {
          if (!teamId) return;
          void upgradeTeamToAnnual
            .mutateAsync({teamId})
            .then(() => {
              setIsAnnualConfirmOpen(false);
              toast.success('Subscription upgraded!');
              void navigate({to: '/', replace: true});
            })
            .catch(() => {
              toast.error('Failed to upgrade subscription');
            });
        }}
        isLoading={upgradeTeamToAnnual.isPending}
        planType={config.annualDialogPlanType}
        seatsCount={teamSeatsCount || 1}
        currentPriceLabel={
          billingDetails
            ? formatPriceLabel(
                billingDetails.priceInCents,
                billingDetails.billingInterval,
              )
            : undefined
        }
      />

      <TierUpgradeConfirmDialog
        open={isTierUpgradeConfirmOpen}
        onOpenChange={setIsTierUpgradeConfirmOpen}
        onConfirm={() => {
          if (!teamId || upgradeTeamToPro.isPending) return;
          void upgradeTeamToPro
            .mutateAsync({teamId, isAnnual: isAnnual || isOnYearly})
            .then(() => {
              setIsTierUpgradeConfirmOpen(false);
              toast.success('Subscription upgraded!');
              void navigate({to: '/', replace: true});
            })
            .catch(() => {
              toast.error('Failed to upgrade subscription');
            });
        }}
        isLoading={upgradeTeamToPro.isPending}
        isAnnual={isAnnual || isOnYearly}
        isTeam
        seatsCount={teamSeatsCount || 1}
        currentPriceLabel={
          billingDetails
            ? formatPriceLabel(
                billingDetails.priceInCents,
                billingDetails.billingInterval,
              )
            : undefined
        }
      />
    </>
  );
};

const teamPlusConfig: Omit<TeamTierCardConfig, 'features'> = {
  tier: 'plus',
  title: 'Team Plus',
  planIcon: 'plus',
  price: `$${pricing.team.plus.monthly}`,
  monthlyPlan: 'team-plus',
  annualPlan: 'team-plus-annual',
  annualDialogPlanType: 'team-plus',
  amplitudeEvent: '[Paywall] team plus plan click',
  annualToggle: {
    monthlyPrice: `$${pricing.team.plus.monthly}`,
    yearlyPrice: `$${pricing.team.plus.yearly}`,
    yearlyMonthlyEquivalent: `$${yearlyMonthlyEquivalent(pricing.team.plus.yearly)}`,
    discountPercent,
  },
};

const teamProConfig: Omit<TeamTierCardConfig, 'features'> = {
  tier: 'pro',
  title: 'Team Pro',
  planIcon: 'pro',
  price: `$${pricing.team.pro.monthly}`,
  monthlyPlan: 'team-pro-unlimited',
  annualPlan: 'team-pro-unlimited-annual',
  annualDialogPlanType: 'team-pro',
  amplitudeEvent: '[Paywall] team pro unlimited plan click',
  annualToggle: {
    monthlyPrice: `$${pricing.team.pro.monthly}`,
    yearlyPrice: `$${pricing.team.pro.yearly}`,
    yearlyMonthlyEquivalent: `$${yearlyMonthlyEquivalent(pricing.team.pro.yearly)}`,
    discountPercent,
  },
};

type TeamCardExternalProps = {
  isAnnual: boolean;
  billingInterval: BillingInterval | undefined;
  individualBillingInterval: BillingInterval | undefined;
  currentTier: SubscriptionTier;
};

export const TeamPlusCard = (props: TeamCardExternalProps) => {
  const {subscriptions} = useSubscription();
  return (
    <TeamTierCard
      config={{
        ...teamPlusConfig,
        features: teamPlusFeatures(
          subscriptions?.settings.maxCreditsPerGeneration ?? 7,
        ),
      }}
      {...props}
    />
  );
};

export const TeamProCard = (props: TeamCardExternalProps) => {
  const {subscriptions} = useSubscription();
  return (
    <TeamTierCard
      config={{
        ...teamProConfig,
        features: proTeamFeatures(
          subscriptions?.settings.maxCreditsPerGeneration ?? 7,
        ),
      }}
      {...props}
    />
  );
};
